import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Pause, Play } from 'lucide-react';
import { EYEBROW, BTN_PRIMARY, BTN_SECONDARY } from './styles';

const SOURCES = ['/assets/hero/knob-sequence-v28.mp4', '/assets/hero/knob-sequence-v28.webm'];
const POSTER = '/assets/hero/frame-rest-light.jpg';

function RevealLine({ children, delay, reduce }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={reduce ? { y: 0, opacity: 1 } : { y: '112%' }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function KnobHero() {
  const videoRef = useRef(null);
  const reduce = useReducedMotion();
  const [failed, setFailed] = useState(false);
  const [srcIdx, setSrcIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  // Mobile (<768px): permanent still of the first knob frame, same crop — no rotation,
  // and the video file is never downloaded on phones.
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  const showVideo = !reduce && !failed && !isMobile;

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const fn = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  const onSourceError = () => {
    if (srcIdx < SOURCES.length - 1) setSrcIdx(srcIdx + 1);
    else setFailed(true);
  };

  useEffect(() => {
    if (reduce && videoRef.current) {
      videoRef.current.pause();
      setPlaying(false);
    }
  }, [reduce]);

  useEffect(() => {
    const v = videoRef.current;
    if (v && playing && v.paused) {
      v.play().catch(() => {});
    }
  }, [srcIdx, playing]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-base"
      aria-labelledby="hero-heading"
      data-testid="hero"
    >
      {showVideo ? (
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover object-[68%_20%] opacity-95 md:object-[62%_20%] lg:object-[50%_20%]"
          src={SOURCES[srcIdx]}
          poster={POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onError={onSourceError}
          data-testid="hero-video"
        />
      ) : (
        <img
          src={POSTER}
          alt="RangeSafe Classic Brushed Stainless safety knob at rest on a dark range panel"
          className="absolute inset-0 h-full w-full object-cover object-[68%_20%] opacity-95 md:object-[62%_20%] lg:object-[50%_20%]"
          data-testid="hero-poster"
        />
      )}

      {/* scrims keep text readable through the whole sequence */}
      <div className="absolute inset-0 bg-gradient-to-r from-base via-base/75 to-base/10 lg:via-base/45" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-base via-base/30 to-transparent" aria-hidden="true" />
      {/* mobile-only dim so the white hero copy stays readable over the still image */}
      <div className="absolute inset-0 bg-base/55 md:hidden" aria-hidden="true" />

      <div className="relative z-10 mx-auto w-full max-w-shell px-5 pb-24 pt-36 sm:px-8 lg:pb-28">
        <h1
          id="hero-heading"
          className="font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight text-white"
          style={{ fontSize: 'clamp(1.55rem, 4.2vw, 3.75rem)' }}
          data-testid="hero-headline"
        >
          <RevealLine delay={0.15} reduce={reduce}>
            RangeSafe
          </RevealLine>
          <RevealLine delay={0.3} reduce={reduce}>
            <span className="text-accent">Commercial Solutions</span>
          </RevealLine>
        </h1>
        <motion.div
          className="mt-7 max-w-lg space-y-4"
          initial={reduce ? {} : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-subline"
        >
          <p className="text-base font-light leading-relaxed text-white sm:text-lg md:text-dim">
            RangeSafe Stove Safety Knobs™ are a proactive, effective and ADA-compliant solution to reduce the risk of accidental actuation of gas and electric stoves that can lead to fires and gas leaks in commercial properties.
          </p>
          <p className="text-base font-light leading-relaxed text-white sm:text-lg md:text-dim">
            RangeSafe Stove Safety Knobs™ also satisfy New York City’s Local Law 44 regulation relating to permanent stove safety knobs with integrated locking mechanisms.
          </p>
          <p className="text-base font-light leading-relaxed text-white sm:text-lg md:text-dim">
            RangeSafe Commercial Solutions offers a no cost, no obligation initial consultation to help prospective commercial clients assess which RangeSafe product is best suited for their needs and help develop pre-purchase pilot programs.
          </p>
        </motion.div>
        <motion.div
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          initial={reduce ? {} : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link to="/quote" className={BTN_PRIMARY} data-testid="hero-quote-cta">
            Request a Consultation
          </Link>
          <Link to="/products" className={BTN_SECONDARY} data-testid="hero-how-cta">
            Our Products
          </Link>
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-5 right-5 z-10 flex items-end justify-end sm:px-3 lg:px-3">
        {showVideo && (
          <button
            type="button"
            onClick={toggle}
            aria-label={playing ? 'Pause hero video' : 'Play hero video'}
            aria-pressed={!playing}
            data-testid="hero-play-pause"
            className="inline-flex h-11 w-11 items-center justify-center border border-line bg-base/60 text-white backdrop-blur transition-colors hover:border-silver"
          >
            {playing ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
          </button>
        )}
      </div>

      <p className="sr-only">
        Video reel for RangeSafe Stove Safety Knobs: a RangeSafe Classic Brushed Stainless Stove
        Safety Knob at rest on a dark range panel, a woman walking out of her apartment kitchen
        past a lit burner, a family and their dog in a dark kitchen and a controlled blue gas
        flame on a burner before the scene returns to the knob at rest. To operate a RangeSafe
        knob, push it toward the stove while pressing both sides, then turn to the desired
        setting.
      </p>
    </section>
  );
}
